import { Link, useParams, useNavigate } from "react-router-dom";
import Footer from "./Footer";
import { useState, useEffect, useRef } from "react";
import Top from "./Top";

// ── Helpers ────────────────────────────────────────────────────────────────
function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  // Already an embed URL
  if (url.includes("youtube.com/embed/")) return url;
  // youtu.be/ID
  const shortMatch = url.match(/youtu\.be\/([A-Za-z0-9_-]{11})/);
  if (shortMatch) return `https://www.youtube.com/embed/${shortMatch[1]}`;
  // watch?v=ID
  const watchMatch = url.match(/[?&]v=([A-Za-z0-9_-]{11})/);
  if (watchMatch) return `https://www.youtube.com/embed/${watchMatch[1]}`;
  return null;
}

function isVideoUrl(url) {
  if (!url) return false;
  return (
    url.includes("youtube.com") ||
    url.includes("youtu.be") ||
    url.includes("vimeo.com") ||
    /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url)
  );
}

function isFileUrl(url) {
  if (!url) return false;
  return /\.(pdf|doc|docx|ppt|pptx|xls|xlsx|zip|png|jpg|jpeg|gif|svg)(\?|$)/i.test(
    url,
  );
}

function getFileIcon(url) {
  if (/\.pdf(\?|$)/i.test(url))
    return { icon: "bi-file-earmark-pdf-fill", color: "#e74c3c", label: "PDF" };
  if (/\.(doc|docx)(\?|$)/i.test(url))
    return {
      icon: "bi-file-earmark-word-fill",
      color: "#2b579a",
      label: "Word",
    };
  if (/\.(ppt|pptx)(\?|$)/i.test(url))
    return { icon: "bi-file-earmark-ppt-fill", color: "#d24726", label: "PPT" };
  if (/\.(xls|xlsx)(\?|$)/i.test(url))
    return {
      icon: "bi-file-earmark-excel-fill",
      color: "#217346",
      label: "Excel",
    };
  if (/\.(png|jpg|jpeg|gif|svg)(\?|$)/i.test(url))
    return {
      icon: "bi-file-earmark-image-fill",
      color: "#8e44ad",
      label: "Image",
    };
  if (/\.zip(\?|$)/i.test(url))
    return { icon: "bi-file-earmark-zip-fill", color: "#e67e22", label: "ZIP" };
  return { icon: "bi-file-earmark-fill", color: "#555", label: "File" };
}

// ── Lesson Content Modal ───────────────────────────────────────────────────
function LessonContentModal({ lesson, onClose }) {
  const [activeVideoIdx, setActiveVideoIdx] = useState(0);
  const [activeTab, setActiveTab] = useState("videos"); // videos | notes
  const modalRef = useRef(null);

  // Close on backdrop click
  const handleBackdrop = (e) => {
    if (e.target === modalRef.current) onClose();
  };

  // Close on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, []);

  if (!lesson) return null;

  const videos = (lesson.videoUrls || []).filter((v) => isVideoUrl(v.url));
  const files = (lesson.notes || []).filter((n) => isFileUrl(n.url || n));
  const activeVideo = videos[activeVideoIdx];
  const embedUrl = activeVideo ? getYouTubeEmbedUrl(activeVideo.url) : null;

  const hasTabs = videos.length > 0 && files.length > 0;

  return (
    <div
      ref={modalRef}
      onClick={handleBackdrop}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 20,
          width: "100%",
          maxWidth: 860,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(0,0,0,0.3)",
          border: "1px solid rgba(0,0,0,0.08)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "18px 20px 0",
            background: "#fff",
          }}
        >
          {/* Top row: icon + title + close */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              paddingBottom: 16,
            }}
          >
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 12,
                background: "#1D9E75",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <i
                className="bi bi-play-fill text-white"
                style={{ fontSize: 18 }}
              />
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <h5
                style={{
                  margin: 0,
                  fontSize: 15,
                  fontWeight: 600,
                  color: "#111",
                  lineHeight: 1.1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {lesson.title?.trim() || "Lesson"}
              </h5>
              <span
                style={{
                  fontSize: 12,
                  color: "#aaa",
                  display: "block",
                }}
              >
                {videos.length} video{videos.length !== 1 ? "s" : ""}
                {files.length > 0
                  ? ` · ${files.length} resource${files.length !== 1 ? "s" : ""}`
                  : ""}
                {lesson.duration ? ` · ${lesson.duration} min` : ""}
              </span>
            </div>

            <button
              onClick={onClose}
              style={{
                background: "#f5f5f5",
                border: "1px solid #ebebeb",
                cursor: "pointer",
                padding: "7px",
                borderRadius: 9,
                color: "#888",
                fontSize: 14,
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#ebebeb")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "#f5f5f5")
              }
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>

          {/* Tabs (only if both exist) */}
          {hasTabs && (
            <div
              style={{
                display: "flex",
                gap: 4,
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              {[
                {
                  key: "videos",
                  label: `Videos (${videos.length})`,
                  icon: "bi-play-circle",
                },
                {
                  key: "notes",
                  label: `Resources (${files.length})`,
                  icon: "bi-file-earmark",
                },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "10px 14px 12px",
                    fontSize: 13,
                    fontWeight: activeTab === t.key ? 600 : 400,
                    color: activeTab === t.key ? "#1D9E75" : "#999",
                    borderBottom:
                      activeTab === t.key
                        ? "2px solid #1D9E75"
                        : "2px solid transparent",
                    marginBottom: -1,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    transition: "color 0.15s",
                  }}
                >
                  <i className={`bi ${t.icon}`} style={{ fontSize: 13 }} />
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {/* ── Videos Tab ── */}
          {(activeTab === "videos" || !hasTabs) && videos.length > 0 && (
            <div>
              {/* Main Player */}
              <div
                style={{
                  background: "#000",
                  position: "relative",
                  paddingTop: "56.25%",
                }}
              >
                {embedUrl ? (
                  <iframe
                    key={embedUrl}
                    src={`${embedUrl}?autoplay=1&rel=0`}
                    title={activeVideo?.title || "Video"}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                    }}
                  />
                ) : (
                  <video
                    key={activeVideo?.url}
                    controls
                    src={activeVideo?.url}
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                    }}
                  />
                )}
              </div>

              {/* Now Playing bar */}
              <div
                style={{
                  padding: "12px 20px 10px",
                  borderBottom: "1px solid #f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: "#1D9E75",
                    background: "#E1F5EE",
                    borderRadius: 4,
                    padding: "2px 7px",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    flexShrink: 0,
                  }}
                >
                  Now playing
                </span>
                <p
                  style={{
                    margin: 0,
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#111",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {activeVideo?.title}
                </p>
              </div>

              {/* Video Playlist */}
              {videos.length > 1 && (
                <div
                  style={{
                    padding: "8px 12px 12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {videos.map((v, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveVideoIdx(i)}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "10px 12px",
                        background:
                          activeVideoIdx === i ? "#E1F5EE" : "transparent",
                        border: "none",
                        borderRadius: 10,
                        cursor: "pointer",
                        textAlign: "left",
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={(e) => {
                        if (activeVideoIdx !== i)
                          e.currentTarget.style.background = "#f7f7f7";
                      }}
                      onMouseLeave={(e) => {
                        if (activeVideoIdx !== i)
                          e.currentTarget.style.background = "transparent";
                      }}
                    >
                      <div
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          background:
                            activeVideoIdx === i ? "#1D9E75" : "#ebebeb",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          transition: "background 0.15s",
                        }}
                      >
                        {activeVideoIdx === i ? (
                          <i
                            className="bi bi-pause-fill"
                            style={{ color: "#fff", fontSize: 13 }}
                          />
                        ) : (
                          <i
                            className="bi bi-play-fill"
                            style={{ color: "#999", fontSize: 12 }}
                          />
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p
                          style={{
                            margin: 0,
                            fontSize: 13,
                            fontWeight: activeVideoIdx === i ? 600 : 400,
                            color: activeVideoIdx === i ? "#1D9E75" : "#333",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {v.title || `Video ${i + 1}`}
                        </p>
                      </div>
                      <span
                        style={{ fontSize: 11, color: "#bbb", flexShrink: 0 }}
                      >
                        {i + 1}/{videos.length}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Notes / Resources Tab ── */}
          {(activeTab === "notes" || !hasTabs) && files.length > 0 && (
            <div
              style={{
                padding: 20,
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <p
                style={{
                  margin: "0 0 4px",
                  fontSize: 11,
                  color: "#aaa",
                  fontWeight: 600,
                  letterSpacing: "0.7px",
                  textTransform: "uppercase",
                }}
              >
                Lesson resources
              </p>
              {files.map((note, i) => {
                const url = typeof note === "string" ? note : note.url;
                const name =
                  note.title ||
                  note.name ||
                  url?.split("/").pop() ||
                  `Resource ${i + 1}`;
                const { icon, color, label } = getFileIcon(url);
                const isImage = /\.(png|jpg|jpeg|gif|svg)(\?|$)/i.test(url);
                const isPdf = /\.pdf(\?|$)/i.test(url);

                return (
                  <div
                    key={i}
                    style={{
                      border: "1px solid #ebebeb",
                      borderRadius: 12,
                      overflow: "hidden",
                      background: "#fff",
                    }}
                  >
                    {/* File header row */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "12px 16px",
                      }}
                    >
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: 9,
                          background: "#f5f5f5",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <i
                          className={`bi ${icon}`}
                          style={{ color, fontSize: 20 }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p
                          style={{
                            margin: 0,
                            fontWeight: 600,
                            fontSize: 13,
                            color: "#111",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {name}
                        </p>
                        <span style={{ fontSize: 11, color: "#bbb" }}>
                          {label} · View in page
                        </span>
                      </div>
                      <button
                        style={{
                          marginLeft: "auto",
                          background: "none",
                          border: "1px solid #ebebeb",
                          borderRadius: 8,
                          padding: "5px 12px",
                          cursor: "pointer",
                          fontSize: 12,
                          color: "#666",
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          flexShrink: 0,
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "#f5f5f5")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "none")
                        }
                      >
                        <i
                          className="bi bi-download"
                          style={{ fontSize: 12 }}
                        />
                        Download
                      </button>
                    </div>

                    {/* Inline preview */}
                    {isImage && (
                      <img
                        src={url}
                        alt={name}
                        style={{
                          width: "100%",
                          maxHeight: 360,
                          objectFit: "contain",
                          background: "#f5f5f5",
                          display: "block",
                        }}
                      />
                    )}
                    {isPdf && (
                      <iframe
                        src={url}
                        title={name}
                        style={{
                          width: "100%",
                          height: 480,
                          border: "none",
                          display: "block",
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── No content ── */}
          {videos.length === 0 && files.length === 0 && (
            <div style={{ padding: 48, textAlign: "center" }}>
              <i
                className="bi bi-hourglass-split"
                style={{ fontSize: 40, color: "#ddd" }}
              />
              <p style={{ marginTop: 12, color: "#bbb", fontSize: 14 }}>
                Content for this lesson is being prepared.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function ExploreCourse() {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("SLNUserDetails") || "{}");
  const token = localStorage.getItem("SLNToken");
  const userId = user?.userId;

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [notLoggedIn, setNotLoggedIn] = useState(false);
  const [currentLessonId, setCurrentLessonId] = useState(null);
  const [openLesson, setOpenLesson] = useState(null); // lesson object for modal
  const [courses, setCourses] = useState([]);

  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewMsg, setReviewMsg] = useState(null);
  const [hasExistingReview, setHasExistingReview] = useState(false);

  const reqHeaders = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: token } : {}),
  };

  useEffect(() => {
    if (!courseId) return;
    setReviewsLoading(true);
    fetch(
      `https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Reviews/GetOrFilterReviews?courseId=${courseId}&userId=`,
      { headers: reqHeaders },
    )
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : [];
        const filtered = list.filter((r) => r.courseId === courseId);
        setReviews(filtered);
        const own = list.find((r) => r.userId === userId);
        if (own) {
          setHasExistingReview(true);
          setSelectedRating(own.rating);
          setReviewText(own.review || "");
        }
      })
      .catch(() => setReviews([]))
      .finally(() => setReviewsLoading(false));
  }, [courseId]);

  const handleReviewSubmit = async () => {
    if (!selectedRating) {
      setReviewMsg({ type: "error", text: "Please select a star rating." });
      return;
    }
    setReviewSubmitting(true);
    setReviewMsg(null);
    try {
      const res = await fetch(
        "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Reviews/AddOrUpdateReview",
        {
          method: "POST",
          headers: reqHeaders,
          body: JSON.stringify({
            courseId,
            userId,
            rating: selectedRating,
            review: reviewText.trim(),
            status: "active",
          }),
        },
      );
      const data = await res.json();
      if (res.ok || data?.success || data?.message) {
        setReviewMsg({
          type: "success",
          text: "Review submitted successfully!",
        });
        setHasExistingReview(true);
        const r2 = await fetch(
          `https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Reviews/GetOrFilterReviews?courseId=${courseId}&userId=`,
          { headers: reqHeaders },
        );
        const d2 = await r2.json();
        const list2 = Array.isArray(d2?.data) ? d2.data : [];
        setReviews(list2.filter((r) => r.courseId === courseId));
      } else {
        throw new Error(data?.message || "Submission failed");
      }
    } catch (e) {
      setReviewMsg({
        type: "error",
        text: e.message || "Something went wrong. Please try again.",
      });
    }
    setReviewSubmitting(false);
  };

  useEffect(() => {
    if (!token || !userId) {
      setNotLoggedIn(true);
      setLoading(false);
    }
  }, [token, userId]);

  useEffect(() => {
    if (!token || !userId || !courseId) return;
    const verifyAndLoad = async () => {
      try {
        setLoading(true);
        const payRes = await fetch(
          `https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Payment/GetPaymentHistory?userId=${userId}`,
          { headers: reqHeaders },
        );
        const payData = await payRes.json();
        const payments = Array.isArray(payData?.data) ? payData.data : [];
        const hasPurchased = payments.some(
          (p) =>
            p.courseId === courseId &&
            (p.status === "SUCCESS" ||
              p.status === "success" ||
              p.paymentStatus === "SUCCESS" ||
              p.paymentStatus === "success"),
        );
        if (!hasPurchased) {
          setAccessDenied(true);
          setLoading(false);
          return;
        }
        const courseRes = await fetch(
          `https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Courses/GetCourseById?courseId=${courseId}`,
          { headers: reqHeaders },
        );
        if (!courseRes.ok) throw new Error("Failed to fetch course");
        const courseData = await courseRes.json();
        setCourse(courseData?.data);
        if (courseData?.data?.lessons?.length > 0)
          setCurrentLessonId(courseData.data.lessons[0].id);
      } catch (err) {
        console.error("ExploreCourse error:", err);
        setAccessDenied(true);
      } finally {
        setLoading(false);
      }
    };
    verifyAndLoad();
  }, [courseId, token, userId]);

  useEffect(() => {
    fetch(
      "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Courses/GetAllCourses?limit=6&isActive=true",
    )
      .then((res) => res.json())
      .then((data) => setCourses(data.data || []))
      .catch((err) => console.error("Courses API error:", err));
  }, []);

  const createSlug = (text) =>
    text
      ?.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-");

  const limitWords = (text, limit) => {
    if (!text) return "";
    const words = text.split(" ");
    return words.length > limit
      ? words.slice(0, limit).join(" ") + "..."
      : text;
  };

  const getPoints = (text) => {
    if (!text) return [];
    return text
      .split("\n")
      .map((item) => item.trim())
      .filter((item) => item);
  };

  const liveSessions = course?.liveSessions || 20;
  const courseModules = course?.courseModules?.length
    ? course.courseModules
    : [
        {
          title: "Module 1",
          image: "/assets/images/home/8.jpg",
          description:
            "Making Your Foundation Strong\nWhy do we need Technical Analysis\nCandlestick Explanation And Patterns\nImportance of Price Action\nDemand & Supply theory",
        },
        {
          title: "Module 2",
          image: "/assets/images/home/5.jpg",
          description:
            "Your journey toward a rule-based trader\nTrading on Multiple Time Frames\nPick a perfect time frame for your style\nHow to scan stock in the live market\nTrend Analysis (Trading In The Zone Special)",
        },
        {
          title: "Module 3",
          image: "/assets/images/home/7.jpg",
          description:
            "Skills to become a pro trader in any market situation\nWhen to be Aggressive or Conservative\nMoving Average Types (SMA, EMA, LWA)\nAlgo Trader (How to take advantage)\nMarket Traps",
        },
      ];
  const offerTitle =
    course?.offerTitle || "To Unlock All Bonuses Worth ₹50,000";
  const offerDeadline = course?.offerDeadline || "08 November 2025";
  const offerDescription = course?.offerDescription || "";
  const demoVideos = course?.demoVideos?.length
    ? course.demoVideos
    : [
        "https://www.youtube.com/embed/CKN1h-aqVf8?si=dab78a-esRGkcF-_",
        "https://www.youtube.com/embed/2xa1kphLoyw?si=_4yVQpUodhJgSsR-",
        "https://www.youtube.com/embed/CKN1h-aqVf8?si=dab78a-esRGkcF-_",
      ];

  const getDemoVideoLayout = (videos) => {
    if (!videos?.length) return [];
    if (videos.length === 1)
      return [{ src: videos[0], col: "col-lg-12", height: "360" }];
    if (videos.length === 2)
      return videos.map((src) => ({ src, col: "col-lg-6", height: "300" }));
    return videos.map((src, i) => {
      const isEdge = i === 0 || i === videos.length - 1;
      if (videos.length === 3)
        return {
          src,
          col: isEdge ? "col-lg-3" : "col-lg-6",
          height: isEdge ? "180" : "320",
        };
      return {
        src,
        col: isEdge ? "col-lg-3" : "col-lg-5",
        height: isEdge ? "200" : "300",
      };
    });
  };

  const benefits = [
    {
      name: "Lifetime mentorship support",
      image: "/assets/images/icons/4.svg",
      subtitle: "Benefit 1",
    },
    {
      name: "Personalized Trading Guidance",
      image: "/assets/images/icons/5.jpg",
      subtitle: "Benefit 2",
    },
    {
      name: "Advanced Stock Market Tools",
      image: "/assets/images/icons/6.svg",
      subtitle: "Benefit 3",
    },
    {
      name: "24x7 Learning Community",
      image: "/assets/images/icons/7.svg",
      subtitle: "Benefit 4",
    },
    {
      name: "Career Growth Opportunities",
      image: "/assets/images/icons/8.jpg",
      subtitle: "Benefit 5",
    },
    {
      name: "Practical Market Insights",
      image: "/assets/images/icons/9.svg",
      subtitle: "Benefit 6",
    },
    {
      name: "Continuous Learning Updates",
      image: "/assets/images/icons/10.svg",
      subtitle: "Benefit 7",
    },
    {
      name: "Bas Or Kya, Jaan Loge",
      image: "/assets/images/icons/4.svg",
      subtitle: "",
    },
  ];

  const careerbenefits = [
    {
      name: "Certification",
      image: "/assets/images/icons/1.svg",
      subtitle: "On successful completion, students receive",
      points: getPoints(course?.certification),
    },
    {
      name: "Career Opportunities",
      image: "/assets/images/icons/2.jpg",
      subtitle: "After completing this course, build your career as",
      points: getPoints(course?.careerOpportunities),
    },
    {
      name: "Benefits",
      image: "/assets/images/icons/3.svg",
      subtitle: "Benefit of doing this course",
      points: getPoints(course?.benefits),
    },
  ];

  // ── Gate 1: Not logged in ────────────────────────────────────────────────
  if (notLoggedIn) {
    return (
      <>
        <Top />
        <section className="py-8 bg-white">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-5 col-md-8 col-12 text-center">
                <div className="mb-4">
                  <div
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: "#fff3cd",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 20px",
                    }}
                  >
                    <i
                      className="bi bi-lock-fill text-warning"
                      style={{ fontSize: 36 }}
                    ></i>
                  </div>
                  <h2 className="fw-bold text-dark mb-2">Login Required</h2>
                  <p className="text-secondary fs-5 lh-sm">
                    You need to be logged in to access your enrolled courses.
                  </p>
                </div>
                <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center">
                  <Link
                    to="/login"
                    className="btn btn-primary px-6 rounded-pill"
                  >
                    <i className="bi bi-box-arrow-in-right me-2"></i>Login
                  </Link>
                  <Link
                    to="/register"
                    className="btn btn-outline-primary px-6 rounded-pill"
                  >
                    Create Account
                  </Link>
                </div>
                <p className="mt-4 text-secondary small">
                  Don't have an account?{" "}
                  <Link to="/register" className="text-primary fw-semibold">
                    Sign up free
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Top />
        <section className="py-8 bg-white">
          <div className="container text-center py-5">
            <div
              className="spinner-border text-success mb-2"
              style={{ width: 48, height: 48 }}
              role="status"
            />
            <p className="text-secondary fs-5">
              Verifying Your Access, Please Wait
            </p>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  if (accessDenied) {
    return (
      <>
        <Top />
        <section className="py-6 bg-white">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-6 col-md-9 col-12 text-center">
                <div className="mb-4">
                  <div
                    style={{
                      width: 90,
                      height: 90,
                      borderRadius: "50%",
                      background: "#f8d7da",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 20px",
                    }}
                  >
                    <i
                      className="bi bi-shield-lock-fill text-danger"
                      style={{ fontSize: 40 }}
                    ></i>
                  </div>
                  <h2 className="fw-bold text-dark mb-2">
                    Course Access Restricted
                  </h2>
                  <p className="text-secondary fs-5 lh-sm mb-4">
                    You haven't purchased this course yet.
                    <br />
                    Buy it to get full lifetime access to all lessons,
                    resources, and your certificate.
                  </p>
                  <div className="bg-light rounded-4 p-4 text-start mb-4 lh-sm">
                    <p className="fw-semibold text-dark mb-3">
                      What you'll get after purchasing:
                    </p>
                    {[
                      "Full access to all lessons & recordings",
                      "Downloadable course certificate",
                      "Lifetime mentorship support",
                      "Notes, checklists & resources",
                    ].map((item, i) => (
                      <div
                        key={i}
                        className="d-flex align-items-center gap-2 mb-2"
                      >
                        <i className="bi bi-check-circle-fill text-success flex-shrink-0"></i>
                        <span className="text-dark">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center">
                  <Link
                    to={`/course/${courseId}/Course`}
                    className="btn btn-success px-6 rounded-pill fw-semibold"
                  >
                    <i className="bi bi-bag-check me-2"></i>Buy This Course
                  </Link>
                  <Link
                    to="/category"
                    className="btn btn-outline-secondary px-6 rounded-pill"
                  >
                    My Courses
                  </Link>
                </div>
                <p className="mt-3 text-secondary small">
                  Already purchased?{" "}
                  <button
                    className="btn btn-link p-0 small text-primary"
                    onClick={() => window.location.reload()}
                  >
                    Refresh page
                  </button>
                </p>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  // ── ✅ Access granted ─────────────────────────────────────────────────────
  return (
    <>
      <Top />

      {/* Lesson Content Modal */}
      {openLesson && (
        <LessonContentModal
          lesson={openLesson}
          onClose={() => setOpenLesson(null)}
        />
      )}

      <div className="bg-success py-2">
        <div className="container d-flex align-items-center justify-content-center gap-2">
          <i className="bi bi-patch-check-fill text-white"></i>
          <p className="text-white fw-medium m-0 small">
            You have full access to this course — happy learning! 🎉
          </p>
        </div>
      </div>

      <section className="pt-8 bg-white-black">
        <div className="container">
          <div className="row gy-5 g-3">
            <div className="col-xl-8 col-12">
              <div className="d-flex flex-column gap-2 product-content">
                <div className="d-flex flex-column gap-3">
                  <div className="d-flex flex-column">
                    <h1 className="mb-0 display-6 text-white">
                      {course?.title}
                    </h1>
                    <p className="mb-0 fs-4 opacity-75 lh-sm text-light">
                      {limitWords(course?.description, 30)}
                    </p>
                  </div>
                  <div className="d-flex flex-column">
                    <div className="d-flex flex-lg-row flex-column gap-2 gap-md-3 align-items-lg-center">
                      <span className="d-flex flex-row gap-2 lh-1 align-items-center">
                        <span className="text-secondary">4.5</span>
                        <span className="align-text-bottom">
                          <i className="bi bi-star-fill text-warning pe-1"></i>
                          <i className="bi bi-star-fill text-warning pe-1"></i>
                          <i className="bi bi-star-fill text-warning pe-1"></i>
                          <i className="bi bi-star-fill text-warning pe-1"></i>
                          <i className="bi bi-star-half text-warning pe-1"></i>
                        </span>
                        <span className="text-primary">(245 ratings)</span>
                      </span>
                      <span className="d-flex flex-row gap-2 align-items-center">
                        <i className="bi bi-people"></i>
                        <span className="text-secondary">2,240 students</span>
                      </span>
                      <span className="d-flex flex-row gap-2 align-items-center">
                        <i className="bi bi-exclamation-circle text-gray-500"></i>
                        <span className="text-secondary">
                          Last updated{" "}
                          {new Date(course?.updatedAt).toLocaleString("en-IN", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                            timeZone: "Asia/Kolkata",
                          })}
                        </span>
                      </span>
                    </div>
                    <div className="d-flex gap-2">
                      <span className="d-flex flex-row gap-2 align-items-center">
                        <i className="bi bi-globe2 text-gray-500"></i>
                        <span className="text-secondary">English</span>
                      </span>
                      <span className="d-flex flex-row gap-1 align-items-center">
                        <svg
                          className="me-1 mt-n1"
                          width={16}
                          height={16}
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect
                            x={3}
                            y={8}
                            width={2}
                            height={6}
                            rx={1}
                            fill="#754FFE"
                          />
                          <rect
                            x={7}
                            y={5}
                            width={2}
                            height={9}
                            rx={1}
                            fill="#754FFE"
                          />
                          <rect
                            x={11}
                            y={2}
                            width={2}
                            height={12}
                            rx={1}
                            fill="#DBD8E9"
                          />
                        </svg>
                        <span className="text-secondary">Intermediate</span>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="card rounded-4 mb-1 px-4">
                    <ul className="nav nav-lt-tab" id="tab" role="tablist">
                      <li className="nav-item mx-0">
                        <a
                          className="nav-link active px-4"
                          id="description-tab"
                          data-bs-toggle="pill"
                          href="#description"
                          role="tab"
                          aria-controls="description"
                          aria-selected="false"
                        >
                          Description
                        </a>
                      </li>
                      <li className="nav-item mx-0">
                        <a
                          className="nav-link px-4"
                          id="review-tab"
                          data-bs-toggle="pill"
                          href="#review"
                          role="tab"
                          aria-controls="review"
                          aria-selected="false"
                        >
                          Reviews
                        </a>
                      </li>
                    </ul>
                  </div>

                  <div className="card rounded-4">
                    <div className="card-body">
                      <div className="tab-content" id="tabContent">
                        <div
                          className="tab-pane fade show active p-3 pt-2"
                          id="description"
                          role="tabpanel"
                          aria-labelledby="description-tab"
                        >
                          <div className="mb-4">
                            <h3 className="mb-2 text-dark ls-1">
                              About the Course
                            </h3>
                            <p className="lh-sm">{course?.about}</p>
                          </div>
                          <h3 className="mb-2 text-dark ls-1">
                            Course Objectives
                          </h3>
                          <div>
                            <p className="lh-sm">{course?.objectives}</p>
                          </div>
                          <h3 className="mb-2 text-dark ls-1">
                            Course Details
                          </h3>
                          <div>
                            <p className="lh-sm">{course?.details}</p>
                          </div>
                        </div>
                        <div
                          className="tab-pane fade p-3 pt-2"
                          id="review"
                          role="tabpanel"
                          aria-labelledby="review-tab"
                        >
                          {(() => {
                            const total = reviews.length;
                            const avg = total
                              ? reviews.reduce(
                                  (s, r) => s + (r.rating || 0),
                                  0,
                                ) / total
                              : 0;
                            const rounded = Math.round(avg * 10) / 10;
                            const counts = [0, 0, 0, 0, 0];
                            reviews.forEach((r) => {
                              if (r.rating >= 1 && r.rating <= 5)
                                counts[r.rating - 1]++;
                            });
                            return (
                              <div className="d-flex gap-4 align-items-center flex-wrap mb-4">
                                <div
                                  className="text-center"
                                  style={{ minWidth: 80 }}
                                >
                                  <div className="display-4 fw-semibold text-dark lh-1">
                                    {total ? rounded.toFixed(1) : "—"}
                                  </div>
                                  <div className="my-1">
                                    {[...Array(5)].map((_, i) => (
                                      <i
                                        key={i}
                                        className={`bi bi-star${i < Math.round(avg) ? "-fill" : ""} text-warning`}
                                      />
                                    ))}
                                  </div>
                                  <small className="text-secondary">
                                    {total
                                      ? `${total} review${total !== 1 ? "s" : ""}`
                                      : "No reviews yet"}
                                  </small>
                                </div>
                                <div
                                  className="flex-grow-1"
                                  style={{ minWidth: 160 }}
                                >
                                  {[5, 4, 3, 2, 1].map((star) => {
                                    const pct = total
                                      ? Math.round(
                                          (counts[star - 1] / total) * 100,
                                        )
                                      : 0;
                                    return (
                                      <div
                                        key={star}
                                        className="d-flex align-items-center gap-2 mb-1"
                                      >
                                        <span
                                          className="text-warning"
                                          style={{
                                            fontSize: 11,
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {[...Array(star)].map((_, i) => (
                                            <i
                                              key={i}
                                              className="bi bi-star-fill"
                                            />
                                          ))}
                                        </span>
                                        <div
                                          className="progress flex-grow-1"
                                          style={{ height: 6 }}
                                        >
                                          <div
                                            className="progress-bar bg-warning"
                                            style={{ width: `${pct}%` }}
                                          />
                                        </div>
                                        <span
                                          className="text-secondary"
                                          style={{
                                            fontSize: 12,
                                            minWidth: 32,
                                            textAlign: "right",
                                          }}
                                        >
                                          {pct}%
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })()}
                          <hr />
                          <div className="mb-4">
                            <h5 className="mb-0 text-dark">
                              {hasExistingReview
                                ? "Update your review"
                                : "Rate this course"}
                            </h5>
                            <div className="d-flex gap-2 mb-3">
                              {[1, 2, 3, 4, 5].map((v) => (
                                <i
                                  key={v}
                                  className={`bi bi-star${(hoverRating || selectedRating) >= v ? "-fill" : ""} text-warning`}
                                  style={{ fontSize: 28, cursor: "pointer" }}
                                  onMouseEnter={() => setHoverRating(v)}
                                  onMouseLeave={() => setHoverRating(0)}
                                  onClick={() => setSelectedRating(v)}
                                />
                              ))}
                            </div>
                            <textarea
                              className="form-control mb-2"
                              rows={3}
                              placeholder="Share your experience with this course…"
                              maxLength={1000}
                              value={reviewText}
                              onChange={(e) => setReviewText(e.target.value)}
                            />
                            {reviewMsg && (
                              <div
                                className={`alert alert-${reviewMsg.type === "success" ? "success" : "danger"} py-2 mb-2`}
                              >
                                {reviewMsg.text}
                              </div>
                            )}
                            <button
                              className="btn btn-success px-4 fw-normal"
                              disabled={reviewSubmitting}
                              onClick={handleReviewSubmit}
                            >
                              {reviewSubmitting
                                ? "Submitting…"
                                : hasExistingReview
                                  ? "Update Review"
                                  : "Submit Review"}
                            </button>
                          </div>
                          <hr />
                          {reviewsLoading ? (
                            <div className="text-center py-4">
                              <div className="spinner-border spinner-border-sm text-success me-2" />
                              <span className="text-secondary">
                                Loading reviews…
                              </span>
                            </div>
                          ) : reviews.length === 0 ? (
                            <p className="text-secondary text-center py-3">
                              No reviews yet — be the first!
                            </p>
                          ) : (
                            <div className="d-flex flex-column gap-1">
                              {reviews.map((r, i) => {
                                const isOwn = r.userId === userId;
                                const name = isOwn
                                  ? "You"
                                  : r.userName || r.userId || "Student";
                                const initials = name
                                  .split(" ")
                                  .map((w) => w[0])
                                  .join("")
                                  .toUpperCase()
                                  .slice(0, 2);
                                return (
                                  <div
                                    key={r.reviewId || r.id || i}
                                    className="border rounded-4 p-3"
                                  >
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                      <div
                                        className="rounded-circle d-flex align-items-center justify-content-center bg-primary text-white fw-semibold"
                                        style={{
                                          width: 36,
                                          height: 36,
                                          fontSize: 13,
                                          flexShrink: 0,
                                        }}
                                      >
                                        {initials}
                                      </div>
                                      <div className="flex-grow-1">
                                        <div className="d-flex align-items-center gap-2">
                                          <span
                                            className="fw-semibold text-dark"
                                            style={{ fontSize: 14 }}
                                          >
                                            {name}
                                          </span>
                                          {isOwn && (
                                            <span
                                              className="badge bg-dark bg-opacity-10 text-white px-3 ms-2"
                                              style={{ fontSize: 11 }}
                                            >
                                              Your review
                                            </span>
                                          )}
                                        </div>
                                        <div
                                          style={{ fontSize: 12 }}
                                          className="text-secondary"
                                        >
                                          {r.createdAt
                                            ? new Date(
                                                r.createdAt,
                                              ).toLocaleDateString("en-IN", {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                              })
                                            : ""}
                                        </div>
                                      </div>
                                      <div className="text-warning">
                                        {[...Array(5)].map((_, j) => (
                                          <i
                                            key={j}
                                            className={`bi bi-star${j < r.rating ? "-fill" : ""}`}
                                          />
                                        ))}
                                      </div>
                                    </div>
                                    {r.review && (
                                      <p className="mb-0 lh-sm text-secondary mt-3">
                                        {r.review}
                                      </p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Lessons List ───────────────────────────────────────── */}
                <div>
                  <div className="card rounded-top-4 rounded-bottom-0 p-3 pb-8">
                    <ul className="list-group rounded-4 list-group-flush">
                      <li className="list-group-item p-0 rounded-4 bg-transparent">
                        <div className="h4 mb-0 d-flex align-items-center pt-3 pb-2 px-4">
                          <div className="me-auto text-dark lh-sm mb-2">
                            Course Lesson Details
                            <p className="m-0 fs-6 fw-normal">
                              {course?.lessons?.length} Lessons
                            </p>
                          </div>
                        </div>

                        <ul className="list-group list-group-flush pb-3">
                          {(course?.lessons || []).map((lesson, lIdx) => {
                            const videoCount = (lesson.videoUrls || []).filter(
                              (v) => isVideoUrl(v.url),
                            ).length;
                            const fileCount = (lesson.notes || []).length;
                            const hasContent = videoCount > 0 || fileCount > 0;

                            return (
                              <li
                                key={lesson.id || lIdx}
                                className="list-group-item rounded-4"
                                style={{
                                  cursor: hasContent ? "pointer" : "default",
                                }}
                              >
                                <button
                                  onClick={() =>
                                    hasContent && setOpenLesson(lesson)
                                  }
                                  className="d-flex justify-content-between align-items-center w-100 text-start border-0 bg-transparent p-0"
                                  style={{
                                    cursor: hasContent ? "pointer" : "default",
                                  }}
                                >
                                  {/* Left: icon + title + meta */}
                                  <div className="d-flex align-items-center gap-2 text-truncate">
                                    <span
                                      className="icon-shape icon-sm rounded-circle flex-shrink-0"
                                      style={{
                                        background: hasContent
                                          ? "#1D9E75"
                                          : "#e0e0e0",
                                        color: "#fff",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: 32,
                                        height: 32,
                                      }}
                                    >
                                      <i
                                        className={`bi ${hasContent ? "bi-play-fill" : "bi-hourglass-split"}`}
                                        style={{ fontSize: 14 }}
                                      ></i>
                                    </span>
                                    <div className="text-truncate">
                                      <span
                                        className="text-dark d-block text-truncate fw-medium"
                                        style={{ fontSize: 14 }}
                                      >
                                        {lesson.title?.trim()}
                                      </span>
                                      {hasContent && (
                                        <span
                                          style={{
                                            fontSize: 11,
                                            color: "#888",
                                          }}
                                        >
                                          {videoCount > 0 &&
                                            `${videoCount} video${videoCount !== 1 ? "s" : ""}`}
                                          {videoCount > 0 &&
                                            fileCount > 0 &&
                                            " · "}
                                          {fileCount > 0 &&
                                            `${fileCount} resource${fileCount !== 1 ? "s" : ""}`}
                                          {lesson.duration
                                            ? ` · ${lesson.duration} min`
                                            : ""}
                                        </span>
                                      )}
                                      {!hasContent && (
                                        <span
                                          style={{
                                            fontSize: 11,
                                            color: "#bbb",
                                          }}
                                        >
                                          Content coming soon
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Right: open indicator */}
                                  {hasContent && (
                                    <span
                                      className="flex-shrink-0 ms-2 d-flex align-items-center gap-1"
                                      style={{
                                        color: "#1D9E75",
                                        fontWeight: 600,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <i className="bi bi-chevron-right"></i>
                                    </span>
                                  )}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="col-xl-4 col-12">
              <div id="sidebar">
                <div className="sidebar__inner">
                  <div className="d-flex flex-column gap-4">
                    <div className="card rounded-4">
                      <a href="#">
                        <img
                          src="/assets/images/1.jpg"
                          alt="Image"
                          className="object-fit-cover w-100 card-img-top rounded-top-4"
                          height="200"
                        />
                      </a>
                      <div className="card-body pt-3 pb-3">
                        <button className="btn btn-success w-100 rounded-3 fw-normal">
                          <i className="bi bi-award me-2"></i>Download
                          Certificate
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="rounded-4 bg-white p-4 d-flex flex-column gap-3 mt-2">
                  <div className="d-flex flex-row align-items-center gap-3">
                    <div className="position-relative">
                      <img
                        src={course?.instructors[0]?.profileImage}
                        alt="avatar"
                        className="rounded-circle object-fit-cover object-position-top bg-light avatar-xl"
                      />
                      <a
                        href="#"
                        className="position-absolute mt-7 ms-n4"
                        data-bs-toggle="tooltip"
                        data-placement="top"
                        title="Verified"
                      >
                        <img
                          src="/assets/images/check.svg"
                          alt="check"
                          height={30}
                          width={30}
                        />
                      </a>
                    </div>
                    <div>
                      <h3 className="mb-0 text-primary lh-1 m-0">
                        {course?.instructors[0]?.name}
                      </h3>
                      <span className="lh-1">Instructor</span>
                    </div>
                  </div>
                  <ul className="list-unstyled mb-0 d-flex flex-column">
                    <li className="d-flex flex-row gap-2">
                      <i className="bi bi-person"></i>
                      <span className="fw-semibold">2,792,124</span> Students
                    </li>
                    <li className="d-flex flex-row gap-2">
                      <i className="bi bi-camera-video"></i>
                      <span className="fw-semibold">34</span> Courses
                    </li>
                  </ul>
                  <p className="mb-3 lh-sm">
                    {limitWords(course?.instructors[0]?.description, 35)}
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Mentorship CTA */}
      <section className="pb-6 position-relative z-1">
        <div className="container">
          <div className="row align-items-center bg-gold gx-0 rounded-4">
            <div className="col-lg-5 col-12 d-none d-lg-block">
              <div className="d-flex justify-content-center">
                <div className="position-relative">
                  <img
                    src="/assets/images/dir.png"
                    alt="image"
                    className="mt-n8"
                    width="230"
                  />
                  <div className="ms-n8 position-absolute bottom-0 start-0 mb-6">
                    <img src="/assets/images/dollor.svg" alt="dollor" />
                  </div>
                  <div className="me-n4 position-absolute top-0 end-0">
                    <img src="/assets/images/graph.svg" alt="graph" />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6 col-12">
              <div className="text-white p-5 p-lg-0">
                <h2 className="h1 text-white fw-normal">
                  Get Lifetime <span className="fw-bold">Mentorship</span>{" "}
                  support
                </h2>
                <div className="text-center mt-n4">
                  <img src="/assets/images/white_border.webp" width="150" />
                </div>
                <p className="mb-0 lead lh-sm">
                  Start Your Career with Professional Training,
                  <br />
                  Certification & Internship.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="py-2">
        <div className="container">
          <div className="row mb-5">
            <div className="col-12">
              <div className="text-center d-flex flex-column gap-2">
                <h2 className="mb-0 h1 lh-1 ls-sm">
                  What Will You Learn Over{" "}
                  <span className="text-primary fw-bold">
                    {liveSessions} Live Sessions
                  </span>
                  ?
                </h2>
                <p className="mb-0 fs-4 lh-1">
                  Master every concept — from basics to pro trading strategies.
                </p>
                <div className="mt-n3 pt-2">
                  <img
                    src="/assets/images/line-shap.webp"
                    width="230"
                    className="mt-2"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="row justify-content-center">
            <div className="col-10">
              <div style={{ position: "relative" }}>
                <div className="progress-container">
                  <div className="progress-bar" />
                </div>
                {courseModules.map((mod, idx) => {
                  const points = getPoints(mod.description);
                  const imgSrc =
                    mod.image ||
                    `/assets/images/home/${["8.jpg", "5.jpg", "7.jpg"][idx % 3]}`;
                  return (
                    <div key={idx} className="row align-items-center mb-5 gy-4">
                      <div className="col-lg-6 px-8">
                        <div className="d-flex gap-3 align-items-start">
                          <div
                            style={{
                              width: 44,
                              height: 44,
                              minWidth: 44,
                              borderRadius: "50%",
                              background: "#1D9E75",
                              color: "#fff",
                              fontSize: 16,
                              fontWeight: 500,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              position: "relative",
                              zIndex: 1,
                            }}
                          >
                            {idx + 1}
                          </div>
                          <div className="d-flex flex-column gap-2 pt-1">
                            <h3 className="mb-0 h2">{mod.title}</h3>
                            <ul className="fs-5">
                              {points.map((point, i) => (
                                <li key={i}>{point}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-6 px-8">
                        <img
                          src={imgSrc}
                          alt={mod.title}
                          className="img-fluid rounded-5 w-100"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Career & Benefits */}
      <section className="pb-7 pt-2">
        <div className="container">
          <div className="card rounded-5 p-7 gradient-green">
            <div className="text-center">
              <span className="text-white d-block text-uppercase fw-semibold ls-xl">
                Mutual Fund Distributors Certification Course
              </span>
              <h2 className="mb-0 lh-1 fw-semibold text-white">
                Career & Benefits
              </h2>
              <div className="mt-n2 pt-1">
                <img
                  src="/assets/images/line-shap.webp"
                  width="230"
                  className="mt-2"
                />
              </div>
            </div>
            <div className="mt-4">
              <div className="row justify-content-center g-2">
                {careerbenefits.map((item, index) => (
                  <div key={index} className="col-lg-4 col-6">
                    <div className="card rounded-5 card-body gap-3 text-center h-100">
                      <div>
                        <img src={item.image} height="120" alt={item.name} />
                      </div>
                      <div>
                        <h4 className="mb-0 lh-sm">{item.name}</h4>
                        <p className="text-gray-500 lh-sm m-0">
                          {item.subtitle}
                        </p>
                        <ul className="text-start mt-3 px-3">
                          {item.points.map((point, i) => (
                            <li key={i} className="text-dark lh-sm mb-1">
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why SLN */}
      <section className="pb-5">
        <div className="container">
          <div className="row">
            <div className="col-12 mx-auto">
              <div className="d-flex flex-column gap-2 mb-4">
                <h2 className="h1 mb-0 lh-1 ls-sm">
                  Why Choose <u className="text-gold">SLN Academy ?</u>
                </h2>
                <p className="m-0 fs-4 lh-1">
                  Empowering your trading journey with expert mentorship,
                  practical tools, and lifelong learning support.
                </p>
                <div className="mt-n3 pt-2">
                  <img
                    src="/assets/images/line-shap.webp"
                    width="230"
                    className="mt-2"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="row g-2">
            {benefits.map((item, index) => (
              <div key={index} className="col-lg-3 col-md-4 col-6">
                <div className="card-hover-svg card rounded-4 card-body d-flex flex-column gap-3 text-center h-100 justify-content-center">
                  <div>
                    <img src={item.image} height="80" alt={item.name} />
                  </div>
                  <div>
                    <h4 className="mb-0 lh-1">{item.name}</h4>
                    <span className="text-gray-500">{item.subtitle}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Offer Banner */}
      <section className="bg-info mt-3 p-7">
        <div className="container">
          {offerTitle && (
            <h5 className="mb-0 fs-3 fw-normal lh-sm text-light">
              {offerTitle}
            </h5>
          )}
          <h2 className="h1 text-white fw-normal ls-sm lh-1">
            Register Before Midnight Of{" "}
            <span className="fw-bold">{offerDeadline}</span>
          </h2>
          {offerDescription && (
            <p className="text-light opacity-75 fs-4 mt-2 mb-0">
              {offerDescription}
            </p>
          )}
          <div className="mt-n4 pt-2">
            <img
              src="/assets/images/line-shap.webp"
              width="230"
              className="mt-2"
            />
          </div>
        </div>
      </section>

      {/* Accordion */}
      <section className="py-2 bg-white">
        <div className="container pt-lg-6">
          <div className="row align-items-center">
            <div className="col-lg-5">
              <div className="row align-items-end g-2 mb-3">
                <div className="col-6">
                  <img
                    src="/assets/images/home/1.jpg"
                    alt=""
                    className="img-fluid rounded-3 w-100 rounded-4"
                  />
                </div>
                <div className="col-6">
                  <img
                    src="/assets/images/home/7.jpg"
                    alt=""
                    className="img-fluid rounded-3 w-100 rounded-4"
                  />
                </div>
              </div>
              <img
                src="/assets/images/home/5.jpg"
                alt=""
                className="img-fluid rounded-3 w-100 rounded-4"
              />
            </div>
            <div className="col-lg-7 col-12">
              <div
                className="accordion accordion-flush ps-lg-6"
                id="accordionExample"
              >
                {[
                  {
                    id: "One",
                    title: "Scalper",
                    content:
                      "This course includes pure technical analysis with a proper price action study, risk management, trend analysis and a complete top down approach which makes it suitable for scalpers to generate very good profits out of trading.",
                    open: true,
                  },
                  {
                    id: "Two",
                    title: "Full Time/Part Time Trader",
                    content:
                      "This course educates you on concepts about technical analysis like demand and supply theory, basic to advanced sector analysis, the Top Down Approach, trend analysis across multiple time frames, gap theory, and indicators like Moving Averages and many more.",
                  },
                  {
                    id: "Three",
                    title: "Investor",
                    content:
                      "This course helps you to identify demand zones of higher time frame which consists of the largest number of institutional orders that leads to a continuous upmove in the stock which ultimately helps you to achieve long term capital growth.",
                  },
                  {
                    id: "Four",
                    title: "Working Professionals",
                    content:
                      "Working professionals who want to generate some extra income from stock market then this is the right course for you. It helps to invest in right assets by identifying the proper demand zones making it easier for working professionals to generate the passive income.",
                  },
                  {
                    id: "Five",
                    title: "Housewives / Students",
                    content:
                      "This courses teach students and housewives how to identify potential investment opportunities that can generate passive income. They can understand various investment instruments and evaluate market trends, which can potentially lead to better returns on their investments.",
                  },
                  {
                    id: "Six",
                    title: "Business Owners / Entrepreneur",
                    content:
                      "This courses teach entrepreneurs and business owners about financial analysis, valuation techniques, and risk management strategies. This knowledge can help them effectively manage their company's finances, make informed investment decisions, and optimize their capital structure.",
                  },
                ].map((item) => (
                  <div
                    key={item.id}
                    className="border p-3 rounded-4 mb-2"
                    id={`heading${item.id}`}
                  >
                    <h3 className="mb-0 fs-4">
                      <a
                        href="#"
                        className="d-flex align-items-center text-dark"
                        data-bs-toggle="collapse"
                        data-bs-target={`#collapse${item.id}`}
                        aria-expanded={item.open ? "true" : "false"}
                        aria-controls={`collapse${item.id}`}
                      >
                        <span className="me-auto">{item.title}</span>
                        <span className="collapse-toggle ms-4">
                          <i className="bi bi-chevron-down" />
                        </span>
                      </a>
                    </h3>
                    <div
                      id={`collapse${item.id}`}
                      className={`collapse ${item.open ? "show" : ""}`}
                      aria-labelledby={`heading${item.id}`}
                      data-bs-parent="#accordionExample"
                    >
                      <div className="pt-2 lh-sm">{item.content}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Videos */}
      {demoVideos.length > 0 && (
        <section className="py-6 bg-white">
          <div className="container">
            <div className="mb-4 pb-1 text-center">
              <h1 className="mb-0 lh-1 display-6 text-dark">
                Live Demo / Video
              </h1>
              <p className="mb-0 fs-4 text-gray-600">
                Empower Your Knowledge. Enrich Your Wealth. Build Your Career
              </p>
            </div>
            <div className="row g-2 d-flex align-items-center">
              {getDemoVideoLayout(demoVideos).map((video, i) => (
                <div key={i} className={`${video.col} col-12`}>
                  <iframe
                    height={video.height}
                    src={video.src}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    className="w-100 object-fit-cover rounded-5"
                  ></iframe>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Similar Courses */}
      <section className="pb-6 bg-white">
        <div className="container mb-2">
          <div className="row">
            <div className="col-12 mx-auto">
              <div className="d-flex flex-column gap-2 mb-4">
                <h2 className="h1 mb-0 lh-1 ls-sm">
                  Similar Courses <u className="text-gold">You May Like</u>
                </h2>
                <p className="m-0 fs-4 lh-1">
                  Explore more programs designed to help you master every aspect
                  of trading.
                </p>
                <div className="mt-n3 pt-2">
                  <img
                    src="/assets/images/line-shap.webp"
                    width="230"
                    className="mt-2"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="row g-2">
            {courses.slice(0, 6).map((c, index) => (
              <div key={index} className="col-lg-4 col-12">
                <div className="row g-0 rounded-5 h-100">
                  <div className="col-12 px-4 d-flex align-items-center bg-light pb-4 pt-1 rounded-4">
                    <div className="d-flex flex-column px-2 mb-2">
                      <div className="d-flex flex-column gap-3 mt-4">
                        <div className="d-flex flex-column">
                          <h3 className="mb-0 lh-1">
                            <Link
                              to={`/course/${c?.courseId}/${createSlug(c?.title)}`}
                              className="fs-3 text-dark"
                            >
                              {c?.title}
                            </Link>
                          </h3>
                          <p className="mb-0 lh-1 mt-1 mb-2">
                            {c?.about?.split(" ").slice(0, 15).join(" ")}...
                          </p>
                          <span className="text-gray-500">
                            By <a href="#">{c?.instructors[0]?.name}</a>
                          </span>
                        </div>
                        <div className="lh-1">
                          <span className="text-secondary fw-semibold">
                            4.5
                          </span>
                          <span className="align-text-top ms-3 me-2">
                            {[...Array(5)].map((_, i) => (
                              <i
                                key={i}
                                className="bi bi-star-fill text-warning"
                              ></i>
                            ))}
                          </span>
                          <span className="text-gray-500">(13,245)</span>
                        </div>
                      </div>
                      <div className="d-flex flex-row gap-2 align-items-center">
                        <span className="fs-3 lh-1 mt-2 fw-semibold text-dark">
                          ₹{c?.offeredPrice} + GST
                        </span>
                      </div>
                      <span className="fw-medium lh-sm text-gray-400">
                        <del>₹{c?.originalPrice?.toLocaleString("en-IN")}</del>
                      </span>
                      <div className="mt-3">
                        <Link
                          to={`/course/${c?.courseId}/${createSlug(c?.title)}`}
                          className="btn bg-gold text-dark w-auto px-5 rounded-pill border-0"
                        >
                          View Course
                        </Link>
                        <Link className="btn btn-icon btn-success rounded-circle d-none d-md-inline-flex ms-1">
                          <i className="bi bi-whatsapp fs-4 align-middle"></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default ExploreCourse;
